import { Link } from 'react-router'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import { getRatingConcentration } from '#app/scoring/rating-concentration.server.ts'
import { describeRatingConcentration } from '#app/scoring/rating-concentration.ts'
import {
	BAYESIAN_PRIOR_RATING,
	BAYESIAN_PRIOR_VOTES,
	CRITICAL_RATING_MAX,
	CRITICAL_RESPONSE_SHARE,
	DEPTH_FLOORS,
	MIN_CITATIONS_PER_CLAIM_PUBLIC,
	MIN_REVIEWS_TO_SCORE,
	POLARIZATION_MAX_PENALTY,
	POLARIZATION_MIN_REVIEWS,
	RECENCY_FULL_CREDIT_DAYS,
	RECENCY_ZERO_CREDIT_DAYS,
	SCORE_FACTORS,
	TRAJECTORY_MIN_PER_WINDOW,
	TRAJECTORY_SATURATION_DELTA,
	TRAJECTORY_WINDOW_DAYS,
	UNKNOWN_FACTOR_VALUE,
	VOLUME_SATURATION,
} from '#app/scoring/scoring-model.ts'
import { SITE_AUTHOR, buildPersonLd } from '#app/seo/author.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { formatPublicDate, formatPublicNumber } from '#app/utils/misc.tsx'
import { type Route } from './+types/scoring-method.ts'

/**
 * Public methodology page.
 *
 * Everything here renders from the constants in app/scoring/scoring-model.ts —
 * the same module that computes the scores. The published methodology cannot
 * drift from the code that produces it, which is the entire point of a
 * methodology page.
 */

export async function loader() {
	const [scored, withSynthesis, reviewCount, lastComputed, concentration] =
		await Promise.all([
			prisma.business.count({ where: { score: { not: null } } }),
			prisma.business.count({ where: { synthesisAt: { not: null } } }),
			prisma.review.count(),
			prisma.business.findFirst({
				where: { scoreComputedAt: { not: null } },
				orderBy: { scoreComputedAt: 'desc' },
				select: { scoreComputedAt: true },
			}),
			// Measured across every scored business, not asserted from the pilot
			// vertical. See rating-concentration.server.ts.
			getRatingConcentration(),
		])

	return {
		stats: {
			scored,
			withSynthesis,
			reviewCount,
			lastComputed: lastComputed?.scoreComputedAt ?? null,
		},
		concentrationCopy: describeRatingConcentration(
			concentration,
			'businesses we score',
		),
	}
}

export const handle = {
	breadcrumb() {
		return { name: 'Scoring method', url: '/scoring-method' }
	},
	seo({ origin }: { origin: string }): SeoProps {
		const canonical = `${origin}/scoring-method`
		const title = 'How we score businesses | DFW Professionals'
		const description =
			'The full ranking methodology behind the DFW Professionals Score: what we weigh, how we weigh it, what we refuse to publish, and what money cannot buy.'

		return {
			title,
			description,
			canonical,
			openGraph: { title, description, type: 'article', url: canonical },
			twitter: { card: 'summary_large_image', title, description },
			/*
			 * The canonical home of the Person node. Every ranking references
			 * this @id rather than restating the author, so a crawler resolves one
			 * editor across the site instead of one per page.
			 */
			structuredData: [buildPersonLd({ origin })],
		}
	},
}

function percent(weight: number) {
	return `${Math.round(weight * 100)}%`
}

export default function ScoringMethodPage({
	loaderData,
}: Route.ComponentProps) {
	const { stats, concentrationCopy } = loaderData

	return (
		<div className="container mx-auto max-w-3xl px-4 py-12">
			<h1 className="mb-4 text-3xl font-medium tracking-tight md:text-4xl">
				How we score businesses
			</h1>
			<p className="text-muted-foreground mb-8 text-lg leading-relaxed">
				Every business on DFW Professionals carries a score from 0 to 100. It is computed
				from public data on a published formula, the same one described on this
				page. No business can pay to raise it, and no business can pay to have
				it removed.
			</p>

			<Card className="mb-10">
				<CardContent className="grid grid-cols-2 gap-6 pt-6 sm:grid-cols-4">
					<div>
						<div className="text-2xl font-medium">{stats.scored}</div>
						<div className="text-muted-foreground text-sm">
							Businesses scored
						</div>
					</div>
					<div>
						<div className="text-2xl font-medium">
							{formatPublicNumber(stats.reviewCount)}
						</div>
						<div className="text-muted-foreground text-sm">
							Reviews analyzed
						</div>
					</div>
					<div>
						<div className="text-2xl font-medium">{stats.withSynthesis}</div>
						<div className="text-muted-foreground text-sm">
							With review analysis
						</div>
					</div>
					<div>
						<div className="text-2xl font-medium">
							{formatPublicDate(stats.lastComputed, 'MMM d') ?? 'Not yet'}
						</div>
						<div className="text-muted-foreground text-sm">Last recomputed</div>
					</div>
				</CardContent>
			</Card>

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				What goes into the score
			</h2>
			<p className="text-muted-foreground mb-6 leading-relaxed">
				{SCORE_FACTORS.length} factors, weighted as follows. Each is scored
				0–100 on its own, then combined. When we have not measured a factor yet,
				it counts as a neutral {Math.round(UNKNOWN_FACTOR_VALUE * 100)} rather
				than a zero, and the listing says so.
			</p>

			<div className="mb-10 space-y-5">
				{SCORE_FACTORS.map((factor) => (
					<div key={factor.key} className="border-l-2 pl-4">
						<div className="mb-1 flex items-baseline justify-between gap-4">
							<h3 className="font-medium">{factor.label}</h3>
							<span className="text-muted-foreground shrink-0 text-sm tabular-nums">
								{percent(factor.weight)}
							</span>
						</div>
						<p className="text-muted-foreground text-sm leading-relaxed">
							{factor.description}
						</p>
					</div>
				))}
			</div>

			<Separator className="my-10" />

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				Why star ratings alone are not enough
			</h2>
			{/*
			 * Measured, not asserted. This paragraph used to state a figure taken
			 * from the roofing pilot as though it described every market on the
			 * site; it now reports what the scored catalog actually shows, and
			 * omits the claim entirely when the sample is too small to support it.
			 */}
			{concentrationCopy ? (
				<p className="text-muted-foreground mb-4 leading-relaxed">
					{concentrationCopy.sentence} The score reads what people actually
					wrote, not the star they clicked.
				</p>
			) : null}
			{/*
			 * "That band" only exists in the concentrated case. When the measured
			 * spread is wide — or when there is no measurement to report at all —
			 * the sentence would be pointing at something the page never
			 * established, so the phrasing changes rather than the paragraph
			 * disappearing: the point it makes is true either way.
			 */}
			<p className="text-muted-foreground mb-4 leading-relaxed">
				{concentrationCopy?.isConcentrated
					? 'A business with hundreds of reviews at the top of that band can still'
					: 'A business with hundreds of reviews and a high star average can still'}{' '}
				have a recurring pattern of missed appointments, unresolved callbacks,
				or billing surprises in the text of those reviews. When that happens,
				the score reflects it and the business ranks lower than its star average
				would suggest.
			</p>

			<Separator className="my-10" />

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				The specifics
			</h2>
			<dl className="mb-10 space-y-4 text-sm">
				<div>
					<dt className="font-medium">Ratings are adjusted for confidence</dt>
					<dd className="text-muted-foreground leading-relaxed">
						A rating is pulled toward a baseline of {BAYESIAN_PRIOR_RATING}{' '}
						until a business has around {BAYESIAN_PRIOR_VOTES} reviews. A 5.0
						from three reviews does not outrank a 4.7 from four hundred.
					</dd>
				</div>
				<div>
					<dt className="font-medium">Review volume has diminishing returns</dt>
					<dd className="text-muted-foreground leading-relaxed">
						Volume is scored on a curve that flattens around {VOLUME_SATURATION}{' '}
						reviews, so a national chain cannot bury a well-reviewed independent
						on count alone.
					</dd>
				</div>
				<div>
					<dt className="font-medium">Recent feedback counts for more</dt>
					<dd className="text-muted-foreground leading-relaxed">
						Reviews from the last {RECENCY_FULL_CREDIT_DAYS} days receive full
						credit, tapering to none beyond{' '}
						{Math.round(RECENCY_ZERO_CREDIT_DAYS / 365)} years. We use the
						median review age, so a single recent review cannot mask years of
						silence.
					</dd>
				</div>
				<div>
					<dt className="font-medium">Direction is measured year over year</dt>
					<dd className="text-muted-foreground leading-relaxed">
						We compare the ratings left in the last{' '}
						{Math.round(TRAJECTORY_WINDOW_DAYS / 365)} year against the year
						before it, and only when both periods hold at least{' '}
						{TRAJECTORY_MIN_PER_WINDOW} reviews. A swing of{' '}
						{TRAJECTORY_SATURATION_DELTA} of a star is treated as a full move.
						Holding steady scores in the middle, not at the bottom, because a
						business that has been consistently good for years is not
						underperforming.
					</dd>
				</div>
				<div>
					<dt className="font-medium">
						Replies to unhappy customers count for most
					</dt>
					<dd className="text-muted-foreground leading-relaxed">
						Of the response score, {Math.round(CRITICAL_RESPONSE_SHARE * 100)}%
						comes from replies to reviews of {CRITICAL_RATING_MAX} stars or
						fewer. Thanking a five star review is easy. A business with no
						critical reviews is scored on its overall reply rate instead, not
						marked down for the absence.
					</dd>
				</div>
				<div>
					<dt className="font-medium">
						A high average can still hide a pattern
					</dt>
					<dd className="text-muted-foreground leading-relaxed">
						Once a business has {POLARIZATION_MIN_REVIEWS} reviews, we look at
						the shape of the histogram, not just the average. A wall of fives
						with a seam of ones behind it is discounted by up to{' '}
						{Math.round(POLARIZATION_MAX_PENALTY * 100)}%. Those one star
						reviews are usually where the watch-outs come from.
					</dd>
				</div>
				<div>
					<dt className="font-medium">Thin evidence means no score</dt>
					<dd className="text-muted-foreground leading-relaxed">
						A business needs at least {MIN_REVIEWS_TO_SCORE} reviews before we
						publish a score. Below that we do not have enough to say anything
						defensible.
					</dd>
				</div>
				<div>
					<dt className="font-medium">
						Missing data is not the same as bad data
					</dt>
					<dd className="text-muted-foreground leading-relaxed">
						When we have not measured a factor yet, it counts as a neutral{' '}
						{Math.round(UNKNOWN_FACTOR_VALUE * 100)} out of 100 and the listing
						shows it as not measured. It is not scored as a zero, because we
						have no evidence the business did badly. It is also not dropped and
						the other weights rescaled, because that would quietly award the
						business its own average on a factor nobody checked, and would mean
						the listings we know the least about score the highest.
					</dd>
				</div>
			</dl>

			<Separator className="my-10" />

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				How we write the pros and watch-outs
			</h2>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				Every claim on a listing, every strength and every caution, has to be
				supported by at least {MIN_CITATIONS_PER_CLAIM_PUBLIC} separate reviews
				before it is published. One person&rsquo;s experience is an anecdote,
				not a pattern.
			</p>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				Claims that fail that test are discarded rather than reworded. This is
				why some businesses show two strengths and others show five, and why
				some show no cautions at all. We do not manufacture balance, and we do
				not pad a page to make it look complete.
			</p>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				When we read reviews, we deliberately include critical ones rather than
				sampling the most recent. A business with two hundred glowing reviews
				and six complaints will have those six read.
			</p>

			<Separator className="my-10" />

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				Pages we refuse to build
			</h2>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				A ranked list is only useful when there is a real field to rank. We do
				not publish a &ldquo;best of&rdquo; page unless we have at least{' '}
				{DEPTH_FLOORS.bestMetro} scored businesses in that category, or{' '}
				{DEPTH_FLOORS.cityCategory} for a single city. Head-to-head comparisons
				require both businesses to be scored and reasonably close.
			</p>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				If we do not have the depth, the page does not exist. There is no
				version of this site where a page lists two businesses and calls it a
				ranking.
			</p>

			<Separator className="my-10" />

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				What money cannot do
			</h2>
			<ul className="text-muted-foreground mb-4 list-disc space-y-2 pl-5 leading-relaxed">
				<li>No business can pay for a higher score or a better rank.</li>
				<li>
					No business can pay to remove a caution that its reviews support.
				</li>
				<li>
					If we ever sell advertising or featured placement, it will be labeled
					as such and excluded from the ranked list.
				</li>
			</ul>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				Businesses can ask us to correct factual errors: a wrong phone number,
				an outdated address, a closed location. They cannot ask us to change a
				rating or remove an accurate summary of what their customers wrote.{' '}
				<Link className="underline underline-offset-4" to="/contact">
					Contact us
				</Link>{' '}
				to report an error.
			</p>

			<Separator className="my-10" />

			{/*
			 * Inclusion, stated publicly, because the rest of this page is a set of
			 * checkable claims about method and "how you got on the list at all"
			 * was the one it did not answer. Owners ask, and the honest answer
			 * happens to be a good one: we use the categories they chose.
			 *
			 * Deliberately no thresholds, no category names and no mention of the
			 * per-category setting. Those are implementation, they invite gaming,
			 * and they would date the moment a flag moves.
			 */}
			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				How a business gets on a list
			</h2>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				Every business here has a Google Business Profile, and every profile has
				categories the owner picked to describe their own work. We use those
				categories to decide which lists a business belongs on. We do not add
				categories a business never claimed.
			</p>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				Most of the time we accept any category on the profile. A general
				contractor who also remodels kitchens belongs on both lists, and we
				would rather show you every real option than only the specialists.
			</p>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				For a few trades we only use the main category the owner chose. A house
				cleaning company and a commercial janitorial company are not the same
				business, even though Google lets both carry the same label. When the
				gap between two trades is that wide, we go with the one the owner listed
				first.
			</p>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				If you run a business and you are missing from a list you should be on,
				or showing up on one you should not be,{' '}
				<Link className="underline underline-offset-4" to="/contact">
					contact us
				</Link>{' '}
				and tell us which category. We will take a look.
			</p>

			<Separator className="my-10" />

			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				Where the data comes from
			</h2>
			<p className="text-muted-foreground mb-4 leading-relaxed">
				Business details and reviews come from public Google Business Profiles.
				Review analysis is produced by software reading that review text and is
				reviewed against the citation rules above before publication. We do not
				invent details about pricing, certifications, service areas, or history.
			</p>
			<p className="text-muted-foreground text-sm leading-relaxed">
				Scores are recomputed as new reviews arrive, so a business&rsquo;s
				position can change without anything on this page changing.
			</p>

			<Separator className="my-10" />

			{/*
			 * The named person behind the method, on the page the Person node's
			 * @id resolves to. A ranked list that names nobody is the easiest kind
			 * of page for a model to skip and the hardest for a reader to argue
			 * with, and an invented editor would be the one lie that discredits
			 * everything else on a site built on checkable claims.
			 */}
			<h2 className="mb-3 text-2xl font-medium tracking-tight">
				Who maintains this
			</h2>
			<p className="text-muted-foreground mb-2 leading-relaxed">
				{SITE_AUTHOR.bio}
			</p>
			<p className="text-muted-foreground text-sm leading-relaxed">
				{SITE_AUTHOR.name}, {SITE_AUTHOR.title}. The rankings themselves are
				computed from the rubric above rather than assembled by hand, so this
				byline claims the method and the standards, not a personal verdict on
				any individual business.
			</p>
		</div>
	)
}
